import React from "react";

import { useDropzone } from "react-dropzone";

import { setSnack } from "@synthesis/shared-ui";

import { useUploadProgress } from "../../file-uploader/file-uploader";
import { useStores } from "../../store";

const DragAndDrop: React.FC = React.memo(() => {
  const { generalStore } = useStores();
  const [isShow, setIsShow] = React.useState<boolean>(false);
  const { startUpload } = useUploadProgress();

  React.useEffect(() => {
    const root = document.getElementById("root");
    if (!root) {
      return;
    }

    const onDragStart = () => setIsShow(true);
    const onDragEnd = () => setIsShow(false);

    root.addEventListener("mouseenter", onDragEnd, false);
    root.addEventListener("mouseleave", onDragEnd, false);
    window.addEventListener("mouseup", onDragEnd, false);
    window.addEventListener("dragenter", onDragStart, false);
    window.addEventListener("drop", onDragEnd, false);

    return () => {
      root.removeEventListener("mouseenter", onDragEnd, false);
      root.removeEventListener("mouseleave", onDragEnd, false);
      window.removeEventListener("mouseup", onDragEnd, false);
      window.removeEventListener("dragenter", onDragStart, false);
      window.removeEventListener("drop", onDragEnd, false);
    };
  }, []);

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (!acceptedFiles.length) {
        return;
      }

      const droppedFile = acceptedFiles[0];
      if (!droppedFile) {
        return;
      }

      const MAX_SIZE_SIZE = 2147483648; // 2GB
      if (droppedFile.size > MAX_SIZE_SIZE) {
        setSnack({
          messageType: "error",
          title: `${droppedFile.name} превышает допустимый размер 2Гб`,
        });
        return;
      }

      if (generalStore.activeTab === "docs") {
        const nonPdfFiles = acceptedFiles.filter((file: File) => !file.name.toLowerCase().endsWith(".pdf"));
        if (nonPdfFiles.length > 0) {
          setSnack({
            messageType: "error",
            title: "Можно загружать только файлы формата PDF",
          });
          return;
        }
        startUpload(acceptedFiles, "pdf");
        return;
      }

      if (generalStore.activeTab === "models") {
        const nonIfcFiles = acceptedFiles.filter((file: File) => !file.name.toLowerCase().endsWith(".ifc"));
        if (nonIfcFiles.length > 0) {
          setSnack({
            messageType: "error",
            title: "Можно загружать только файл формата ifc",
          });
          return;
        }
        startUpload(acceptedFiles, "ifc");
      }
    },
    [generalStore.activeTab, startUpload],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    noClick: true,
  });

  return (
    <div data-cy="dragAndDrop-container" {...getRootProps()} className="absolute inset-0 z-10" style={{ display: isShow ? "block" : "none" }}>
      <input data-cy="dragAndDrop-input" {...getInputProps()} />
      <div className="absolute inset-0 z-10 mt-[5.3125rem] bg-gray-200/25 p-4">
        <div className="flex h-full items-center justify-center rounded-lg border-4 border-dashed bg-gray-200/25">
          {isDragActive ? "Отпустите файл для загрузки" : "Поместите файл в рамку для загрузки"}
        </div>
      </div>
    </div>
  );
});

export default DragAndDrop;
